/* Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  ChannelType,
  useCreateNotificationGroupMutation,
  WebhookChannelType,
} from "~/generated/graphql";
import { showError } from "~/services/toast";
import { PAGER_DUTY_URL } from "~/views/notificationGroups/constants";
import { NotificationGroupData } from "~/views/notificationGroups/NotificationGroupsCreate/components/NotificationGroupsCreateWizard/constants";

type Props = {
  onSuccess: (id: string) => void;
};

const postMethod = "POST";

export function useCreateNotificationGroup({ onSuccess }: Props) {
  const [createNotificationGroupMutation, { loading }] =
    useCreateNotificationGroupMutation({
      onCompleted: (data) => {
        if (data.createNotificationGroup) {
          onSuccess(data?.createNotificationGroup.id);
        }
      },
      onError: (error) => {
        showError(error.message);
      },
      update(cache, { data }, { variables }) {
        if (data?.createNotificationGroup && variables?.input) {
          cache.modify({
            fields: {
              notificationGroups: (existing) => {
                return {
                  notificationGroups: [existing, variables.input],
                };
              },
            },
          });

          cache.gc();
        }
      },
    });

  async function createNotificationGroup(data: NotificationGroupData) {
    const prepareEmailChannels = data.channels?.email_channels?.map(
      ({ email }) => {
        return {
          type: ChannelType.Email,
          typeProperties: {
            email: { email },
          },
        };
      }
    );

    const prepareSlackChannels = data.channels?.slack_channels?.map(
      ({ webhookUrl }) => {
        return {
          type: ChannelType.Webhook,
          typeProperties: {
            webhook: {
              method: postMethod,
              type: WebhookChannelType.Slack,
              url: webhookUrl,
            },
          },
        };
      }
    );

    const preparePagerDutyChannels = data.channels?.pager_duty_channels?.map(
      ({ pagerDutyKey }) => {
        return {
          type: ChannelType.Webhook,
          typeProperties: {
            webhook: {
              method: postMethod,
              type: WebhookChannelType.PagerDuty,
              url: PAGER_DUTY_URL,
              payload: JSON.stringify({ routing_key: pagerDutyKey }),
            },
          },
        };
      }
    );

    const prepareNotificationGroup = {
      description: data?.details?.description ?? "",
      name: data?.details?.name ?? "",
      channels: [
        ...(prepareEmailChannels || []),
        ...(prepareSlackChannels || []),
        ...(preparePagerDutyChannels || []),
      ],
    };

    createNotificationGroupMutation({
      variables: {
        input: prepareNotificationGroup,
      },
    });
  }

  return { createNotificationGroup, loading } as const;
}
